import authLogger from "./authLogger";

export type AuthEvent =
  | "LOGIN"
  | "LOGIN_INHERITED"
  | "LOGOUT"
  | "LOGOUT_ALL"
  | "REFRESH"
  | "TOKEN_REFRESHED"
  | "SESSION_REVOKED";

export interface SyncEvent {
  type: AuthEvent;
  origin: "portal-senne-exames" | "portal-senneliquor" | "unknown";
  ts: number;
  payload?: unknown;
}

const CHANNEL_NAME = "senne-auth";
const ORIGIN: SyncEvent["origin"] = "portal-senne-exames";

type Listener = (event: SyncEvent) => void;

class SessionSyncService {
  private channel: BroadcastChannel | null = null;
  private listeners = new Set<Listener>();

  private ensureChannel(): BroadcastChannel | null {
    if (this.channel) return this.channel;
    if (typeof window === "undefined" || typeof BroadcastChannel === "undefined") {
      return null;
    }
    this.channel = new BroadcastChannel(CHANNEL_NAME);
    this.channel.onmessage = (event: MessageEvent<SyncEvent>) => {
      const msg = event.data;
      if (!msg || typeof msg !== "object" || !msg.type) return;
      authLogger.log("sync", `← recebido ${msg.type} de ${msg.origin}`, {
        ts: new Date(msg.ts).toISOString(),
        payload: msg.payload,
      });
      this.listeners.forEach((listener) => {
        try {
          listener(msg);
        } catch (err) {
          authLogger.error("sync", "listener error", err);
        }
      });
    };
    authLogger.log("sync", `BroadcastChannel('${CHANNEL_NAME}') aberto`);
    return this.channel;
  }

  /**
   * Notifica outras abas (e o portal-senneliquor via mesmo canal) sobre um
   * evento de autenticação.
   */
  emit(type: AuthEvent, payload?: unknown) {
    const ch = this.ensureChannel();
    if (!ch) {
      authLogger.warn("sync", `emit(${type}) ignorado — canal indisponível`);
      return;
    }
    const msg: SyncEvent = { type, origin: ORIGIN, ts: Date.now(), payload };
    try {
      ch.postMessage(msg);
      authLogger.log("sync", `→ emit ${type}`, payload ?? "");
    } catch (err) {
      authLogger.error("sync", `postMessage(${type}) falhou`, err);
    }
  }

  /**
   * Inscreve um listener para reagir a eventos de outras abas / outros portais.
   * Substitui o handler anterior (compatível com a API legada que usava onmessage).
   */
  subscribe(callback: Listener) {
    this.ensureChannel();
    this.listeners.clear();
    this.listeners.add(callback);
  }

  /**
   * Inscreve um listener adicional sem remover os existentes.
   * Retorna unsubscribe.
   */
  on(listener: Listener): () => void {
    this.ensureChannel();
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  destroy() {
    this.listeners.clear();
    if (this.channel) {
      this.channel.close();
      this.channel = null;
    }
  }
}

export default new SessionSyncService();
